import { useCallback } from 'react'
import {
  Drawer,
  VStack,
  HStack,
  Text,
  Box,
  Heading,
  IconButton,
  Card,
  Badge,
  Table,
  MenuRoot,
  MenuTrigger,
  MenuPositioner,
  MenuContent,
  MenuItem,
  Separator,
} from '@chakra-ui/react'
import {
  X,
  ChevronUp,
  ChevronDown,
  MoreVertical,
  CreditCard,
  Link as LinkIcon,
  Users,
  AlertTriangle,
} from 'lucide-react'
import { useOwnerAccountLines } from '../hooks'
import { LoadingState } from '@/shared/components/LoadingState'
import { ErrorState } from '@/shared/components/ErrorState'
import type { OwnerBalance } from '../types'

interface OwnerAccountDetailDrawerProps {
  unitGroup: {
    unitId: string
    unitNumber: string
    accountCode: string
    accountId: string | null
    owners: OwnerBalance[]
    totalBalance: number
    hasMultipleOwners: boolean
  } | null
  residenceId: string
  isOpen: boolean
  onClose: () => void
  onNavigatePrevious?: () => void
  onNavigateNext?: () => void
  hasPrevious?: boolean
  hasNext?: boolean
  // Actions
  onNewPayment?: (unitGroup: OwnerAccountDetailDrawerProps['unitGroup']) => void
  onSendPaymentLink?: (unitGroup: OwnerAccountDetailDrawerProps['unitGroup'], owner?: OwnerBalance) => void
}

export function OwnerAccountDetailDrawer({
  unitGroup,
  residenceId,
  isOpen,
  onClose,
  onNavigatePrevious,
  onNavigateNext,
  hasPrevious = false,
  hasNext = false,
  onNewPayment,
  onSendPaymentLink,
}: OwnerAccountDetailDrawerProps) {
  const { data: lines, isLoading, error } = useOwnerAccountLines(
    unitGroup?.unitId,
    residenceId
  )

  const handleInteractOutside = useCallback(
    (e: { detail: { originalEvent: { target: EventTarget | null } } }) => {
      const target = e.detail.originalEvent.target as HTMLElement
      const tableRow = target.closest('[data-owner-account-row]') as HTMLElement
      if (tableRow) return
      onClose()
    },
    [onClose]
  )

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(value)
  }

  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })
  }

  const getBalanceColor = (balance: number) => {
    if (balance > 0) return 'red.600'
    if (balance < 0) return 'green.600'
    return 'gray.600'
  }

  const getBalanceBadge = (balance: number) => {
    if (balance > 0) {
      return (
        <Badge colorPalette="red" variant="subtle" size="lg">
          Débiteur : {formatCurrency(balance)}
        </Badge>
      )
    }
    if (balance < 0) {
      return (
        <Badge colorPalette="green" variant="subtle" size="lg">
          Créditeur : {formatCurrency(Math.abs(balance))}
        </Badge>
      )
    }
    return (
      <Badge colorPalette="gray" variant="subtle" size="lg">
        Soldé
      </Badge>
    )
  }

  const getOwnershipBadge = (ownershipType: string | null) => {
    if (!ownershipType || ownershipType === 'full') return null
    const labels: Record<string, string> = {
      usufruct: 'USU',
      bare: 'NP',
    }
    const colors: Record<string, string> = {
      usufruct: 'purple',
      bare: 'blue',
    }
    return (
      <Badge colorPalette={colors[ownershipType] || 'gray'} variant="outline" size="sm">
        {labels[ownershipType] || ownershipType}
      </Badge>
    )
  }

  // Construire le nom des propriétaires pour l'affichage
  const getOwnersDisplay = () => {
    if (!unitGroup?.owners.length) return 'Sans propriétaire'
    return unitGroup.owners
      .filter((o) => o.owner_id && o.first_name)
      .map((o) => `${o.first_name} ${o.last_name}`)
      .join(' & ')
  }

  return (
    <Drawer.Root
      open={isOpen}
      placement="end"
      size="lg"
      modal={false}
      onInteractOutside={handleInteractOutside}
    >
      <Drawer.Positioner pointerEvents="none">
        <Drawer.Content maxW="900px" pointerEvents="auto">
          <Drawer.Header borderBottomWidth="1px" py={4} overflow="visible">
            <Box display="flex" alignItems="center" w="full" gap={3}>
              {/* Navigation */}
              <Box display="flex" gap={1} flexShrink={0}>
                <IconButton
                  variant="ghost"
                  size="sm"
                  onClick={onNavigatePrevious}
                  disabled={!hasPrevious}
                  aria-label="Précédent"
                >
                  <ChevronUp size={18} />
                </IconButton>
                <IconButton
                  variant="ghost"
                  size="sm"
                  onClick={onNavigateNext}
                  disabled={!hasNext}
                  aria-label="Suivant"
                >
                  <ChevronDown size={18} />
                </IconButton>
              </Box>

              {/* Title */}
              <Box flex={1} minW={0}>
                {unitGroup && (
                  <>
                    <HStack gap={2} mb={1}>
                      <Badge variant="outline" size="sm">
                        Lot {unitGroup.unitNumber}
                      </Badge>
                      <Badge variant="outline" size="sm" fontFamily="mono">
                        {unitGroup.accountCode}
                      </Badge>
                      {unitGroup.hasMultipleOwners && (
                        <Badge colorPalette="orange" variant="subtle" size="sm">
                          <Users size={12} style={{ marginRight: 4 }} />
                          {unitGroup.owners.length} propriétaires
                        </Badge>
                      )}
                    </HStack>
                    <Heading
                      size="md"
                      lineHeight="1.3"
                      overflow="hidden"
                      textOverflow="ellipsis"
                      whiteSpace="nowrap"
                    >
                      {getOwnersDisplay()}
                    </Heading>
                  </>
                )}
                {!unitGroup && <Heading size="md">Détail du compte</Heading>}
              </Box>

              {/* Actions */}
              <Box display="flex" gap={2} flexShrink={0}>
                {unitGroup && (
                  <MenuRoot positioning={{ placement: 'bottom-end' }} lazyMount unmountOnExit>
                    <MenuTrigger asChild>
                      <IconButton variant="ghost" size="sm" aria-label="Actions">
                        <MoreVertical size={18} />
                      </IconButton>
                    </MenuTrigger>
                    <MenuPositioner>
                      <MenuContent minW="220px" zIndex={9999}>
                        <MenuItem value="new-payment" onClick={() => onNewPayment?.(unitGroup)}>
                          <HStack gap={2}>
                            <CreditCard size={16} />
                            <Text>Nouveau règlement</Text>
                          </HStack>
                        </MenuItem>
                        <Separator />
                        {unitGroup.owners.length === 1 ? (
                          <MenuItem
                            value="send-link"
                            onClick={() => onSendPaymentLink?.(unitGroup, unitGroup.owners[0])}
                          >
                            <HStack gap={2}>
                              <LinkIcon size={16} />
                              <Text>Envoyer un lien de paiement</Text>
                            </HStack>
                          </MenuItem>
                        ) : (
                          unitGroup.owners
                            .filter((o) => o.owner_id && o.first_name)
                            .map((owner) => (
                              <MenuItem
                                key={owner.owner_id}
                                value={`send-link-${owner.owner_id}`}
                                onClick={() => onSendPaymentLink?.(unitGroup, owner)}
                              >
                                <HStack gap={2}>
                                  <LinkIcon size={16} />
                                  <Text fontSize="sm">
                                    Lien de paiement → {owner.first_name} {owner.last_name}
                                  </Text>
                                </HStack>
                              </MenuItem>
                            ))
                        )}
                      </MenuContent>
                    </MenuPositioner>
                  </MenuRoot>
                )}
                <IconButton variant="ghost" size="sm" onClick={onClose} aria-label="Fermer">
                  <X size={18} />
                </IconButton>
              </Box>
            </Box>
          </Drawer.Header>

          <Drawer.Body p={0} display="flex" flexDirection="column" height="100%">
            {isLoading && <LoadingState message="Chargement..." />}
            {error && <ErrorState message="Compte non trouvé" />}

            {unitGroup && !isLoading && !error && (
              <VStack align="stretch" gap={0} flex={1} height="100%">
                {/* Solde et alertes */}
                <Box px={6} pt={4} pb={3} bg="gray.50" borderBottomWidth="1px">
                  <HStack justify="space-between" align="center" flexWrap="wrap" gap={3}>
                    {getBalanceBadge(unitGroup.totalBalance)}

                    {unitGroup.totalBalance > 500 && (
                      <HStack gap={2} color="red.600">
                        <AlertTriangle size={16} />
                        <Text fontSize="sm" fontWeight="medium">
                          Impayé important
                        </Text>
                      </HStack>
                    )}
                  </HStack>

                  {/* Détail par propriétaire si multi-propriétaires */}
                  {unitGroup.hasMultipleOwners && (
                    <Box mt={3}>
                      <Text fontSize="xs" color="gray.600" mb={2}>
                        Répartition par propriétaire :
                      </Text>
                      <HStack gap={4} flexWrap="wrap">
                        {unitGroup.owners
                          .filter((o) => o.owner_id && o.first_name)
                          .map((owner) => (
                            <HStack key={owner.owner_id} gap={2}>
                              <Text fontSize="sm">
                                {owner.first_name} {owner.last_name}
                              </Text>
                              {getOwnershipBadge(owner.ownership_type)}
                              <Text
                                fontSize="sm"
                                fontWeight="semibold"
                                color={getBalanceColor(owner.balance || 0)}
                              >
                                {formatCurrency(owner.balance || 0)}
                              </Text>
                            </HStack>
                          ))}
                      </HStack>
                    </Box>
                  )}
                </Box>

                {/* Liste des écritures */}
                <Box px={6} pt={4} flex={1} overflow="auto">
                  <Card.Root variant="outline">
                    <Card.Header py={3} bg="gray.50" borderBottomWidth="1px">
                      <HStack justify="space-between">
                        <Heading size="sm">Historique des opérations</Heading>
                        <Text fontSize="sm" color="gray.600">
                          {lines?.length || 0} écriture{(lines?.length || 0) > 1 ? 's' : ''}
                        </Text>
                      </HStack>
                    </Card.Header>
                    <Card.Body p={0}>
                      {(!lines || lines.length === 0) ? (
                        <Box py={8} textAlign="center" color="gray.500">
                          <Text>Aucune écriture sur ce compte</Text>
                        </Box>
                      ) : (
                        <Table.Root size="sm">
                          <Table.Header>
                            <Table.Row>
                              <Table.ColumnHeader w="90px">Date</Table.ColumnHeader>
                              <Table.ColumnHeader w="80px">Pièce</Table.ColumnHeader>
                              <Table.ColumnHeader>Libellé</Table.ColumnHeader>
                              <Table.ColumnHeader textAlign="right" w="100px">
                                Débit
                              </Table.ColumnHeader>
                              <Table.ColumnHeader textAlign="right" w="100px">
                                Crédit
                              </Table.ColumnHeader>
                              <Table.ColumnHeader textAlign="right" w="100px">
                                Solde
                              </Table.ColumnHeader>
                              <Table.ColumnHeader w="60px">Lettr.</Table.ColumnHeader>
                            </Table.Row>
                          </Table.Header>
                          <Table.Body>
                            {lines.map((line) => (
                              <Table.Row
                                key={line.id}
                                opacity={line.status === 'draft' ? 0.6 : 1}
                              >
                                <Table.Cell fontSize="sm">
                                  {formatDate(line.transaction_date)}
                                </Table.Cell>
                                <Table.Cell fontFamily="mono" fontSize="xs" color="gray.600">
                                  {line.piece_number || '-'}
                                </Table.Cell>
                                <Table.Cell>
                                  <Text fontSize="sm" lineClamp={1}>
                                    {line.label || line.description}
                                  </Text>
                                  {line.status === 'draft' && (
                                    <Badge size="xs" colorPalette="gray" variant="subtle">
                                      Brouillon
                                    </Badge>
                                  )}
                                </Table.Cell>
                                <Table.Cell
                                  textAlign="right"
                                  fontFamily="mono"
                                  fontSize="sm"
                                  color={line.debit > 0 ? 'red.600' : undefined}
                                >
                                  {line.debit > 0 ? formatCurrency(line.debit) : ''}
                                </Table.Cell>
                                <Table.Cell
                                  textAlign="right"
                                  fontFamily="mono"
                                  fontSize="sm"
                                  color={line.credit > 0 ? 'green.600' : undefined}
                                >
                                  {line.credit > 0 ? formatCurrency(line.credit) : ''}
                                </Table.Cell>
                                <Table.Cell
                                  textAlign="right"
                                  fontFamily="mono"
                                  fontSize="sm"
                                  fontWeight="semibold"
                                  color={getBalanceColor(line.balance)}
                                >
                                  {formatCurrency(line.balance)}
                                </Table.Cell>
                                <Table.Cell>
                                  {line.lettrage_codes && line.lettrage_codes.length > 0 && (
                                    <Badge size="sm" colorPalette="blue" variant="subtle">
                                      {line.lettrage_codes.join(', ')}
                                    </Badge>
                                  )}
                                </Table.Cell>
                              </Table.Row>
                            ))}
                          </Table.Body>
                        </Table.Root>
                      )}
                    </Card.Body>
                  </Card.Root>
                </Box>
              </VStack>
            )}
          </Drawer.Body>
        </Drawer.Content>
      </Drawer.Positioner>
    </Drawer.Root>
  )
}
